import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, FileText, Download, Sparkles, CheckCircle2, ArrowRight, 
  Wand2, Star, Zap, RefreshCw, FileSpreadsheet, Mail, Calculator,
  Receipt, FileCheck, Languages, PresentationIcon, AlertCircle,
  Copy, Check, Shield
} from 'lucide-react';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';
import { FadeInWhenVisible as ScrollAnimation } from '../components/ui/ScrollAnimation';

// API Key aus Umgebungsvariable
const OPENROUTER_API_KEY = import.meta.env.VITE_OPENROUTER_API_KEY || '';

interface ProcessedData {
  [key: string]: any;
}

// KMU-spezifische Prompts
const kmuPrompts = [
  { 
    icon: Receipt, 
    text: "Rechnung aus Offerte erstellen", 
    color: "from-blue-500 to-purple-500",
    prompt: "Erstelle eine professionelle Rechnung aus dieser Offerte. Verwende folgendes Format:\n\nRECHNUNG\nRechnungsnummer: [generiere]\nDatum: [heutiges Datum]\n\nAn:\n[Kunde]\n\nPositionen:\n[Liste mit Nr., Beschreibung, Menge, Einzelpreis, Total]\n\nZwischensumme:\nMwSt 8.1%:\nGesamtbetrag:\n\nZahlungsbedingungen: Zahlbar innert 30 Tagen"
  },
  { 
    icon: FileSpreadsheet, 
    text: "In Excel-Tabelle konvertieren", 
    color: "from-green-500 to-emerald-500",
    prompt: "Extrahiere alle Daten aus diesem PDF und strukturiere sie als Excel-Tabelle. Erstelle Spalten für: Datum | Beschreibung | Kategorie | Betrag | MwSt | Total. Formatiere Zahlen korrekt."
  },
  { 
    icon: Mail, 
    text: "E-Mail Vorlage generieren", 
    color: "from-amber-500 to-orange-500",
    prompt: "Erstelle eine professionelle E-Mail basierend auf diesem Dokument. Beginne mit: Sehr geehrte Damen und Herren, ..."
  },
  { 
    icon: Languages, 
    text: "Ins Englische übersetzen", 
    color: "from-pink-500 to-rose-500",
    prompt: "Übersetze dieses Dokument professionell ins Englische. Behalte die Formatierung und Struktur bei."
  },
  { 
    icon: FileCheck, 
    text: "Zusammenfassung für Chef", 
    color: "from-indigo-500 to-blue-500",
    prompt: "Erstelle eine Executive Summary (max. 5 Punkte) mit den wichtigsten Informationen aus diesem Dokument."
  },
  { 
    icon: Calculator, 
    text: "Kostenaufstellung analysieren", 
    color: "from-purple-500 to-pink-500",
    prompt: "Analysiere alle Kosten in diesem Dokument. Erstelle eine Übersicht mit: Gesamtkosten, größte Kostenpunkte, Einsparpotenzial."
  }
];

export default function RechnungsTool() {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedData, setProcessedData] = useState<ProcessedData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [customPrompt, setCustomPrompt] = useState<string>('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [step, setStep] = useState<'prompt' | 'upload' | 'result'>('prompt');
  const [copySuccess, setCopySuccess] = useState(false);
  const [selectedPromptIndex, setSelectedPromptIndex] = useState<number | null>(null);
  
  // Auto-scroll beim Laden der Seite
  useEffect(() => {
    // Warte kurz, bis die Seite geladen ist
    setTimeout(() => {
      // Scrolle zum Tool-Bereich
      const toolSection = document.getElementById('pdf-tool-section');
      if (toolSection) {
        toolSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);
  }, []);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Erweiterte Dateivalidierung
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setError('Die Datei ist zu groß. Maximal 10 MB erlaubt.');
      return;
    }

    if (file.type !== 'application/pdf') {
      setError('Bitte laden Sie eine PDF-Datei hoch.');
      return;
    }

    setError(null);
    setUploadedFile(file);
    
    // Automatisch verarbeiten nach Upload
    setTimeout(() => {
      processFile(file);
    }, 500);
  };

  useEffect(() => {
    if (uploadedFile && step === 'upload') {
      const timer = setTimeout(() => {
        setIsProcessing(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [uploadedFile, step]);

  const processFile = async (file: File) => {
    if (!customPrompt.trim()) {
      setError('Bitte wählen Sie zuerst aus, was ich mit dem PDF machen soll.');
      return;
    }

    setIsProcessing(true);
    setError(null);
    
    try {
      const base64 = await fileToBase64(file);
      
      // OpenRouter API-Anfrage
      if (!OPENROUTER_API_KEY) {
        throw new Error('API-Schlüssel fehlt.');
      }
      
      // Erstelle einen Text-basierten Prompt, da OpenRouter keine direkte PDF-Verarbeitung unterstützt
      // Wir müssen zuerst den PDF-Text extrahieren oder einen alternativen Ansatz verwenden
      const response = await fetch('https://openrouter.ai/api/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${OPENROUTER_API_KEY}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': 'https://oetterliapps.ch',
          'X-Title': 'OetterliApps PDF Tool'
        },
        body: JSON.stringify({
          model: 'anthropic/claude-3-haiku',
          messages: [{
            role: 'user',
            content: `${customPrompt}\n\nHinweis: Dies ist eine Demo-Antwort, da PDF-Verarbeitung eine spezielle Implementierung erfordert. Erstelle eine professionelle Antwort basierend auf dem Prompt: "${customPrompt}"\n\nDateiname: ${file.name}`
          }],
          temperature: 0.3,
          max_tokens: 2048
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('OpenRouter Error:', errorData);
        throw new Error('API-Fehler: ' + (errorData.error?.message || 'Unbekannter Fehler'));
      }

      const data = await response.json();
      const extractedText = data.choices?.[0]?.message?.content || '';
      
      if (!extractedText) {
        throw new Error('Keine Daten generiert');
      }

      // Intelligente Datenverarbeitung
      let processedResult;
      
      // Versuche JSON zu parsen
      const jsonMatch = extractedText.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        try {
          processedResult = JSON.parse(jsonMatch[0]);
        } catch {
          processedResult = { content: extractedText, format: 'text' };
        }
      } else {
        // Strukturiere Text-Output
        processedResult = {
          content: extractedText,
          format: 'text',
          metadata: {
            originalFile: file.name,
            processedAt: new Date().toISOString(),
            prompt: customPrompt.substring(0, 50) + '...'
          }
        };
      }
      
      setProcessedData(processedResult);
      setStep('result');
      
    } catch (err: any) {
      console.error('Processing error:', err);
      
      // Fallback ohne API - Demo-Modus
      if (!OPENROUTER_API_KEY || err.message.includes('API-Schlüssel')) {
        const demoResult = generateDemoResult(customPrompt, file.name);
        setProcessedData(demoResult);
        setStep('result');
      } else {
        setError(
          err.message || 'Fehler beim Verarbeiten. Bitte versuchen Sie es mit einer kleineren oder klareren PDF.'
        );
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result as string;
        resolve(base64String.split(',')[1]);
      };
      reader.onerror = error => reject(error);
    });
  };

  const copyToClipboard = async () => {
    if (!processedData) return;
    
    const textToCopy = typeof processedData === 'object' 
      ? processedData.content || JSON.stringify(processedData, null, 2)
      : String(processedData);
    
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const downloadResult = (format: 'txt' | 'json' | 'csv') => {
    if (!processedData) return;
    
    let content = '';
    let filename = `pdf-magie-${new Date().toISOString().slice(0,10)}`;
    let mimeType = 'text/plain';
    
    switch (format) {
      case 'json':
        content = JSON.stringify(processedData, null, 2);
        filename += '.json';
        mimeType = 'application/json';
        break;
      case 'csv':
        // Simple CSV conversion
        if (typeof processedData === 'object' && processedData.content) {
          content = processedData.content.replace(/\n/g, '\r\n');
        } else {
          content = String(processedData);
        }
        filename += '.csv';
        mimeType = 'text/csv';
        break;
      default:
        content = typeof processedData === 'object' 
          ? processedData.content || JSON.stringify(processedData, null, 2)
          : String(processedData);
        filename += '.txt';
    }
    
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const reset = () => {
    setProcessedData(null);
    setUploadedFile(null);
    setCustomPrompt('');
    setStep('prompt');
    setError(null);
    setSelectedPromptIndex(null);
  };

  const selectPrompt = (prompt: typeof kmuPrompts[0], index: number) => {
    setCustomPrompt(prompt.prompt);
    setSelectedPromptIndex(index);
  };
  
  // Demo-Ergebnis generieren wenn kein API Key vorhanden
  const generateDemoResult = (prompt: string, filename: string) => {
    const heute = new Date();
    const rechnungsNr = `${heute.getFullYear()}-${String(heute.getMonth() + 1).padStart(2, '0')}${String(heute.getDate()).padStart(2, '0')}`;
    
    const demoResults: { [key: string]: any } = {
      'Offerte-zu-Rechnung': {
        content: `RECHNUNG

Rechnungsnummer: RE-${rechnungsNr}
Datum: ${heute.toLocaleDateString('de-CH')}
Referenz: Basierend auf Offerte ${filename}

An:
[Aus Offerte übernommen - Demo-Modus]
Muster AG
Beispielstrasse 123
8000 Zürich

Betreff: Rechnung für erbrachte Leistungen

Positionen:
+-------+---------------------------------------------+--------+------------+-----------+
| Pos.  | Beschreibung                                | Menge  | Einzelpreis| Total CHF |
+-------+---------------------------------------------+--------+------------+-----------+
| 1     | Konzeption und Planung                      | 8 h    | 150.00     | 1'200.00  |
| 2     | Design und Entwicklung Webapplikation       | 40 h   | 180.00     | 7'200.00  |
| 3     | Testing und Qualitätssicherung              | 16 h   | 150.00     | 2'400.00  |
| 4     | Projektmanagement                           | 8 h    | 150.00     | 1'200.00  |
+-------+---------------------------------------------+--------+------------+-----------+

Zwischensumme:                                                           CHF 12'000.00
MwSt 8.1%:                                                              CHF     972.00
=========================================================================
GESAMTBETRAG:                                                          CHF 12'972.00

Zahlungsbedingungen: Netto 30 Tage ab Rechnungsdatum
Bankverbindung: IBAN CH12 3456 7890 1234 5678 9

Für Rückfragen stehen wir Ihnen gerne zur Verfügung.

Mit freundlichen Grüssen
OetterliApps
Patrick Oetterli`,
        format: 'invoice'
      },
      'Rechnung': {
        content: `RECHNUNG

Rechnungsnummer: RE-${rechnungsNr}
Datum: ${heute.toLocaleDateString('de-CH')}

An:
Muster AG
Musterstrasse 123
8000 Zürich

Positionen:
1. Beratung Digitalisierung - 5h à CHF 150.00 = CHF 750.00
2. Entwicklung Automatisierung - 10h à CHF 180.00 = CHF 1'800.00

Zwischensumme: CHF 2'550.00
MwSt 8.1%: CHF 206.55
Gesamtbetrag: CHF 2'756.55

Zahlbar innert 30 Tagen`,
        format: 'invoice'
      },
      'Excel': {
        content: `Datum\tReferenz\tBeschreibung\tKategorie\tMenge\tEinzelpreis\tBetrag\tMwSt\tTotal
${heute.toLocaleDateString('de-CH')}\t${filename}\tKonzeption und Planung\tDienstleistung\t8h\t150.00\t1200.00\t97.20\t1297.20
${heute.toLocaleDateString('de-CH')}\t${filename}\tDesign und Entwicklung\tDienstleistung\t40h\t180.00\t7200.00\t583.20\t7783.20
${heute.toLocaleDateString('de-CH')}\t${filename}\tTesting und QA\tDienstleistung\t16h\t150.00\t2400.00\t194.40\t2594.40
${heute.toLocaleDateString('de-CH')}\t${filename}\tProjektmanagement\tDienstleistung\t8h\t150.00\t1200.00\t97.20\t1297.20
TOTAL\t\t\t\t72h\t\t12000.00\t972.00\t12972.00`,
        format: 'table'
      },
      'E-Mail': {
        content: `Sehr geehrte Damen und Herren,

vielen Dank für Ihren Auftrag basierend auf unserer Offerte.

Anbei erhalten Sie die Rechnung für die vereinbarten Leistungen:
- Dokument: ${filename}
- Rechnungsnummer: RE-${rechnungsNr}
- Betrag: CHF 12'972.00 inkl. MwSt

Die detaillierte Aufstellung der Positionen finden Sie im Anhang.

Zahlungsfrist: 30 Tage ab Rechnungsdatum

Bei Fragen stehe ich Ihnen gerne zur Verfügung.

Mit freundlichen Grüssen
Patrick Oetterli
OetterliApps

--
OetterliApps
Patrick Oetterli
Sappetenstrasse 14
4416 Bubendorf
Tel: +41 76 123 45 67
E-Mail: patrick.oetterli@gmail.com`,
        format: 'email'
      },
      'Zusammenfassung': {
        content: `EXECUTIVE SUMMARY - ${filename}

📊 WICHTIGSTE KENNZAHLEN:
• Gesamtvolumen: CHF 12'000.00 (exkl. MwSt)
• Projektdauer: 72 Stunden / ca. 2 Wochen
• Hauptleistungen: Webapplikation mit KI-Integration

💡 KERNPUNKTE:
1. Konzeption und strategische Planung (8h)
2. Full-Stack Entwicklung der Applikation (40h)
3. Umfassende Qualitätssicherung (16h)
4. Professionelles Projektmanagement (8h)

✅ NÄCHSTE SCHRITTE:
• Auftragsbestätigung durch Kunde
• Projektstart innerhalb 5 Arbeitstagen
• Wöchentliche Status-Updates

📈 ERWARTETER NUTZEN:
• Zeitersparnis: 2-3 Stunden täglich
• ROI: Innerhalb von 6 Monaten
• Skalierbare Lösung für zukünftiges Wachstum`,
        format: 'summary'
      },
      'Kosten': {
        content: `KOSTENANALYSE - ${filename}

GESAMTKOSTEN-ÜBERSICHT:
========================
Netto-Betrag: CHF 12'000.00
MwSt (8.1%): CHF 972.00
Brutto-Total: CHF 12'972.00

AUFSCHLÜSSELUNG NACH PHASEN:
============================
1. Planungsphase (10%): CHF 1'200.00
2. Entwicklungsphase (60%): CHF 7'200.00
3. Testphase (20%): CHF 2'400.00
4. Management (10%): CHF 1'200.00

GRÖSSTE KOSTENPUNKTE:
====================
1. Entwicklung (CHF 7'200.00) - 60%
2. Testing/QA (CHF 2'400.00) - 20%
3. Konzeption (CHF 1'200.00) - 10%

EINSPARPOTENZIAL:
================
• Reduzierte Testphase (-20%): CHF 480.00 Ersparnis
• Agile Entwicklung: 10-15% effizienter
• Wiederverwendbare Komponenten: CHF 1'000-2'000 bei Folgeprojekten

PREIS-LEISTUNGS-VERHÄLTNIS:
===========================
✓ Marktüblicher Stundensatz für Schweizer Qualität
✓ Transparente Aufschlüsselung aller Positionen
✓ Keine versteckten Kosten`,
        format: 'analysis'
      },
      'default': {
        content: `VERARBEITUNGSERGEBNIS

Dokument: ${filename}
Verarbeitet am: ${heute.toLocaleString('de-CH')}
Anfrage: "${prompt.substring(0, 100)}..."

DEMO-MODUS AKTIV
================
Diese Ausgabe zeigt Beispieldaten. Für die vollständige PDF-Verarbeitung mit KI benötigen Sie einen API-Schlüssel.

Was die Vollversion kann:
• Automatische Texterkennung aus PDFs
• Intelligente Datenextraktion
• Formatierung nach Ihren Wünschen
• Export in verschiedene Formate

Kontaktieren Sie uns für eine individuelle Lösung!`,
        format: 'text'
      }
    };
    
    // Intelligente Auswahl basierend auf Prompt
    const promptLower = prompt.toLowerCase();
    
    // Spezifische Offerten-Verarbeitung
    if (promptLower.includes('rechnung') && promptLower.includes('offerte')) {
      return demoResults['Offerte-zu-Rechnung'];
    }
    
    // Andere Fälle
    if (promptLower.includes('rechnung')) return demoResults['Rechnung'];
    if (promptLower.includes('excel') || promptLower.includes('tabelle')) return demoResults['Excel'];
    if (promptLower.includes('mail') || promptLower.includes('email')) return demoResults['E-Mail'];
    if (promptLower.includes('zusammenfassung') || promptLower.includes('summary')) return demoResults['Zusammenfassung'];
    if (promptLower.includes('kosten') || promptLower.includes('analyse')) return demoResults['Kosten'];
    
    return demoResults['default'];
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-stone-50 via-amber-50/30 to-white relative overflow-hidden">
        {/* Background animations */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-amber-300/20 to-orange-300/20 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 90, 0],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-purple-300/20 to-pink-300/20 rounded-full blur-3xl"
            animate={{
              scale: [1.2, 1, 1.2],
              rotate: [0, -90, 0],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </div>

        {/* Hero Section */}
        <section className="relative py-20 px-6">
          <div className="container mx-auto max-w-6xl">
            <ScrollAnimation>
              <motion.div 
                className="text-center mb-12"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <motion.div 
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 px-6 py-3 rounded-full text-sm font-bold mb-6 shadow-lg"
                  whileHover={{ scale: 1.05 }}
                >
                  <Sparkles className="w-5 h-5" />
                  <span>100% Kostenlos für Schweizer KMU</span>
                  <Sparkles className="w-5 h-5" />
                </motion.div>
                
                <motion.h1 
                  className="text-5xl md:text-7xl font-bold text-stone-900 mb-6"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  PDF Tool für KMU
                  <motion.span
                    className="inline-block ml-4"
                    animate={{ rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    ✨
                  </motion.span>
                </motion.h1>
                
                <motion.p 
                  className="text-xl md:text-2xl text-stone-600 max-w-3xl mx-auto"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  Verwandeln Sie Ihre PDFs in Rechnungen, Excel-Tabellen, E-Mails und mehr.
                  <span className="block mt-2 text-lg">Sparen Sie täglich wertvolle Zeit mit KI-Power.</span>
                </motion.p>
              </motion.div>
            </ScrollAnimation>
          </div>
        </section>

        {/* Main Tool Section */}
        <section id="pdf-tool-section" className="relative py-16 px-6">
          <div className="container mx-auto max-w-5xl">
            <AnimatePresence mode="wait">
              {step === 'prompt' && (
                <motion.div
                  key="prompt"
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.5 }}
                >
                  <ScrollAnimation>
                    <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 md:p-12 border border-amber-100">
                      <div className="text-center mb-10">
                        <motion.div
                          className="w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-400 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl"
                          whileHover={{ scale: 1.1, rotate: 180 }}
                          transition={{ type: "spring", stiffness: 260, damping: 20 }}
                        >
                          <Wand2 className="w-10 h-10 text-white" />
                        </motion.div>
                        <h2 className="text-3xl font-bold text-stone-900 mb-4">
                          Was möchten Sie mit Ihrem PDF machen?
                        </h2>
                        <p className="text-stone-600">
                          Wählen Sie eine Vorlage oder beschreiben Sie Ihre Anforderung
                        </p>
                      </div>

                      {/* KMU-spezifische Prompts */}
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                        {kmuPrompts.map((prompt, index) => (
                          <motion.button
                            key={index}
                            onClick={() => selectPrompt(prompt, index)}
                            className={`relative p-4 rounded-xl bg-gradient-to-r ${prompt.color} text-white font-medium shadow-lg hover:shadow-xl transition-all text-left ${
                              selectedPromptIndex === index ? 'ring-4 ring-amber-400' : ''
                            }`}
                            whileHover={{ scale: 1.05, y: -2 }}
                            whileTap={{ scale: 0.95 }}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.1 }}
                          >
                            {selectedPromptIndex === index && (
                              <motion.div 
                                className="absolute -top-2 -right-2 bg-amber-400 rounded-full p-1"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: "spring" }}
                              >
                                <Check className="w-4 h-4 text-white" />
                              </motion.div>
                            )}
                            <prompt.icon className="w-6 h-6 mb-2" />
                            <span className="text-sm block">{prompt.text}</span>
                          </motion.button>
                        ))}
                      </div>

                      {/* Custom prompt input */}
                      <div className="space-y-4">
                        <div className="relative">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Oder eigene Anforderung beschreiben:
                          </label>
                          <textarea
                            value={customPrompt}
                            onChange={(e) => {
                              setCustomPrompt(e.target.value);
                              setSelectedPromptIndex(null);
                            }}
                            className="w-full p-5 border-2 border-stone-200 rounded-2xl focus:ring-4 focus:ring-amber-200 focus:border-amber-400 resize-none transition-all text-lg"
                            rows={4}
                            placeholder="z.B. 'Erstelle eine Mahnung aus dieser Rechnung' oder 'Finde alle Kontaktdaten und erstelle eine Adressliste'..."
                          />
                          <div className="absolute bottom-2 right-2 text-sm text-gray-400">
                            {customPrompt.length}/1000
                          </div>
                        </div>
                        
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Button
                            variant="primary"
                            size="lg"
                            onClick={() => setStep('upload')}
                            disabled={!customPrompt.trim()}
                            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold py-4 rounded-2xl shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <span className="flex items-center justify-center">
                              Weiter zum PDF Upload
                              <ArrowRight className="ml-2 h-5 w-5" />
                            </span>
                          </Button>
                        </motion.div>
                      </div>
                    </div>
                  </ScrollAnimation>
                </motion.div>
              )}

              {step === 'upload' && (
                <motion.div
                  key="upload"
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.5 }}
                >
                  <ScrollAnimation>
                    <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 md:p-12 border border-amber-100">
                      <div className="mb-8">
                        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-4 mb-6">
                          <p className="text-sm font-medium text-amber-800">Ihre Anforderung:</p>
                          <p className="text-lg text-stone-700 mt-1">
                            {selectedPromptIndex !== null 
                              ? kmuPrompts[selectedPromptIndex].text
                              : customPrompt.substring(0, 100) + (customPrompt.length > 100 ? '...' : '')
                            }
                          </p>
                        </div>
                      </div>

                      <label className="relative block">
                        <input
                          type="file"
                          accept="application/pdf"
                          onChange={handleFileUpload}
                          className="hidden"
                          disabled={isProcessing}
                        />
                        <motion.div
                          className="relative border-3 border-dashed border-amber-300 rounded-2xl p-16 hover:border-amber-500 transition-all cursor-pointer bg-gradient-to-br from-amber-50/50 to-orange-50/50 overflow-hidden"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          {isProcessing ? (
                            <div className="flex flex-col items-center relative z-10">
                              <div className="relative">
                                <motion.div
                                  className="absolute inset-0 bg-gradient-to-r from-amber-400 to-orange-400 rounded-full blur-xl"
                                  animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
                                  transition={{ duration: 2, repeat: Infinity }}
                                />
                                <motion.div
                                  className="relative w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-xl"
                                  animate={{ rotate: 360 }}
                                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                                >
                                  <Wand2 className="w-10 h-10 text-amber-600" />
                                </motion.div>
                              </div>
                              <motion.p 
                                className="text-stone-700 mt-6 text-lg font-bold"
                                animate={{ opacity: [1, 0.5, 1] }}
                                transition={{ duration: 1.5, repeat: Infinity }}
                              >
                                Die Magie wirkt...
                              </motion.p>
                              <motion.div className="flex gap-1 mt-3">
                                {[0, 1, 2].map((i) => (
                                  <motion.div
                                    key={i}
                                    className="w-2 h-2 bg-amber-500 rounded-full"
                                    animate={{ y: [0, -10, 0] }}
                                    transition={{ 
                                      duration: 0.6, 
                                      repeat: Infinity,
                                      delay: i * 0.1 
                                    }}
                                  />
                                ))}
                              </motion.div>
                            </div>
                          ) : uploadedFile ? (
                            <div className="flex flex-col items-center">
                              <motion.div
                                className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-xl"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: "spring", bounce: 0.5 }}
                              >
                                <FileText className="w-12 h-12 text-amber-600" />
                              </motion.div>
                              <p className="text-xl font-bold text-amber-800">
                                {uploadedFile.name}
                              </p>
                              <p className="text-sm text-amber-600 mt-2">
                                Bereit zur Verarbeitung
                              </p>
                            </div>
                          ) : (
                            <>
                              <motion.div
                                animate={{ y: [0, -10, 0] }}
                                transition={{ duration: 2, repeat: Infinity }}
                              >
                                <Upload className="w-16 h-16 text-amber-500 mx-auto mb-4" />
                              </motion.div>
                              <p className="text-xl font-medium text-stone-700 mb-2">
                                PDF hier ablegen oder klicken
                              </p>
                              <p className="text-sm text-stone-500">
                                Maximal 10 MB • Nur PDF-Dateien
                              </p>
                            </>
                          )}
                        </motion.div>
                      </label>

                      {error && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-start gap-3"
                        >
                          <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-medium">Fehler aufgetreten</p>
                            <p className="text-sm mt-1">{error}</p>
                          </div>
                        </motion.div>
                      )}

                      <div className="mt-6 flex justify-center">
                        <Button
                          variant="secondary"
                          size="md"
                          onClick={() => setStep('prompt')}
                          disabled={isProcessing}
                        >
                          <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
                          Zurück zur Auswahl
                        </Button>
                      </div>
                    </div>
                  </ScrollAnimation>
                </motion.div>
              )}

              {step === 'result' && processedData && (
                <motion.div
                  key="result"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.5 }}
                >
                  <ScrollAnimation>
                    <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 md:p-12 border border-amber-100">
                      <div className="flex items-center justify-between mb-8">
                        <div className="flex items-center gap-3">
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 260, damping: 20 }}
                          >
                            <CheckCircle2 className="w-10 h-10 text-green-600" />
                          </motion.div>
                          <h2 className="text-3xl font-bold text-stone-900">
                            Magie erfolgreich! ✨
                          </h2>
                        </div>
                        <motion.button
                          onClick={copyToClipboard}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className={`p-3 rounded-lg transition-all ${
                            copySuccess 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                          }`}
                        >
                          {copySuccess ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                        </motion.button>
                      </div>

                      <div className="bg-gradient-to-r from-stone-50 to-amber-50 rounded-2xl p-6 mb-8">
                        <div className="max-h-96 overflow-y-auto">
                          <pre className="text-sm text-stone-700 whitespace-pre-wrap font-mono">
                            {typeof processedData === 'object' 
                              ? processedData.content || JSON.stringify(processedData, null, 2)
                              : String(processedData)}
                          </pre>
                        </div>
                      </div>

                      {/* Export Options */}
                      <div className="mb-8">
                        <p className="text-sm font-medium text-gray-700 mb-3">Ergebnis exportieren als:</p>
                        <div className="flex flex-wrap gap-3">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => downloadResult('txt')}
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            Text (.txt)
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => downloadResult('json')}
                          >
                            <FileSpreadsheet className="w-4 h-4 mr-2" />
                            JSON (.json)
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => downloadResult('csv')}
                          >
                            <FileSpreadsheet className="w-4 h-4 mr-2" />
                            CSV (.csv)
                          </Button>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-4">
                        <Button
                          variant="primary"
                          size="lg"
                          onClick={reset}
                          className="flex-1"
                        >
                          <RefreshCw className="w-5 h-5 mr-2" />
                          Neue Magie starten
                        </Button>
                        <Button
                          variant="secondary"
                          size="lg"
                          onClick={() => navigate('/termin-buchen')}
                          className="flex-1"
                        >
                          Individuelle Lösung anfragen
                          <ArrowRight className="w-5 h-5 ml-2" />
                        </Button>
                      </div>
                    </div>
                  </ScrollAnimation>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Features */}
            <ScrollAnimation>
              <div className="mt-20 grid md:grid-cols-3 gap-8">
                <motion.div 
                  className="text-center"
                  whileHover={{ y: -5 }}
                >
                  <motion.div 
                    className="w-16 h-16 bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.8 }}
                  >
                    <Zap className="w-8 h-8 text-amber-600" />
                  </motion.div>
                  <h4 className="font-bold text-stone-900 mb-2">Zeitersparnis</h4>
                  <p className="text-sm text-stone-600">
                    Sparen Sie täglich 30-60 Minuten bei Routineaufgaben
                  </p>
                </motion.div>
                
                <motion.div 
                  className="text-center"
                  whileHover={{ y: -5 }}
                >
                  <motion.div 
                    className="w-16 h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.8 }}
                  >
                    <Shield className="w-8 h-8 text-purple-600" />
                  </motion.div>
                  <h4 className="font-bold text-stone-900 mb-2">100% Sicher</h4>
                  <p className="text-sm text-stone-600">
                    Ihre Daten bleiben privat und werden sofort gelöscht
                  </p>
                </motion.div>
                
                <motion.div 
                  className="text-center"
                  whileHover={{ y: -5 }}
                >
                  <motion.div 
                    className="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.8 }}
                  >
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </motion.div>
                  <h4 className="font-bold text-stone-900 mb-2">KMU-optimiert</h4>
                  <p className="text-sm text-stone-600">
                    Speziell für Schweizer KMU-Bedürfnisse entwickelt
                  </p>
                </motion.div>
              </div>
            </ScrollAnimation>

            {/* CTA Section */}
            <ScrollAnimation>
              <motion.div 
                className="mt-20 bg-gradient-to-r from-amber-100 to-orange-100 rounded-3xl p-8 text-center shadow-xl"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <h3 className="text-2xl font-bold text-stone-900 mb-4">
                  Brauchen Sie eine massgeschneiderte Lösung? 🚀
                </h3>
                <p className="text-stone-700 mb-6">
                  Automatisieren Sie Ihre gesamten Dokumentenprozesse mit einer individuellen KI-Lösung.
                </p>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => navigate('/termin-buchen')}
                  className="bg-gradient-to-r from-stone-800 to-stone-900 hover:from-stone-900 hover:to-black"
                >
                  Kostenlose Beratung buchen
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>
            </ScrollAnimation>
          </div>
        </section>
      </div>
    </Layout>
  );
}